import { useMemo, useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Eye, CheckCircle2, AlertCircle } from "lucide-react";
import { isVimeo, isVimeoEmbed, isYouTube, toVimeoEmbed, normalizeVideoUrl, looksLikeHtml } from "@/lib/videoUrl";

/**
 * Reusable Vimeo / YouTube URL input that:
 *  - auto-converts pasted Vimeo URLs to player.vimeo.com embed form on save
 *  - validates URL form
 *  - offers a Preview button that opens an inline 16:9 embed
 *
 * Props:
 *   value     — current saved URL (may be Vimeo embed, YouTube watch, or internal /api/files)
 *   onChange  — receives the *normalised* (embed-ready) URL
 *   helperText, label, testId
 */
export function VimeoUrlField({
  value = "",
  onChange,
  label = "Vimeo URL",
  helperText = "Upload your video to Vimeo first, then paste the Vimeo link here. The video will play inside the LMS.",
  testId = "vimeo-url-field",
}) {
  const [raw, setRaw] = useState(value || "");
  const [showPreview, setShowPreview] = useState(false);

  const status = useMemo(() => {
    const trimmed = (raw || "").trim();
    if (!trimmed) return { kind: "empty" };
    if (looksLikeHtml(trimmed)) return { kind: "html" };
    if (isVimeo(trimmed) || isVimeoEmbed(trimmed)) {
      const embed = toVimeoEmbed(trimmed);
      return embed ? { kind: "valid-vimeo", embed } : { kind: "invalid" };
    }
    if (isYouTube(trimmed)) return { kind: "valid-youtube" };
    if (/^https?:\/\//i.test(trimmed)) return { kind: "valid-other" };
    return { kind: "invalid" };
  }, [raw]);

  const handleChange = (val) => {
    setRaw(val);
    setShowPreview(false);
    // Refuse to forward HTML to parent — keep saved value clean
    if (looksLikeHtml(val)) {
      onChange("");
      return;
    }
    onChange(normalizeVideoUrl(val));
  };

  return (
    <div className="space-y-2" data-testid={testId}>
      <Label className="text-sm font-medium">{label}</Label>
      <Input
        type="text"
        value={raw}
        onChange={(e) => handleChange(e.target.value)}
        placeholder="https://vimeo.com/123456789"
        data-testid={`${testId}-input`}
      />

      <div className="flex flex-wrap items-center gap-2 text-xs">
        {status.kind === "valid-vimeo" && (
          <span className="inline-flex items-center gap-1 text-teal" data-testid="vimeo-status-valid">
            <CheckCircle2 className="h-3.5 w-3.5" /> Vimeo URL Valid
          </span>
        )}
        {status.kind === "valid-youtube" && (
          <span className="inline-flex items-center gap-1 text-muted-foreground">
            <CheckCircle2 className="h-3.5 w-3.5" /> YouTube URL dikesan
          </span>
        )}
        {status.kind === "valid-other" && (
          <span className="inline-flex items-center gap-1 text-muted-foreground">
            <CheckCircle2 className="h-3.5 w-3.5" /> URL diterima
          </span>
        )}
        {status.kind === "invalid" && (
          <span className="inline-flex items-center gap-1 text-destructive" data-testid="vimeo-status-invalid">
            <AlertCircle className="h-3.5 w-3.5" /> Please enter a valid Vimeo video URL.
          </span>
        )}
        {status.kind === "html" && (
          <span className="inline-flex items-center gap-1 text-destructive" data-testid="vimeo-status-html">
            <AlertCircle className="h-3.5 w-3.5" /> Please paste a Vimeo link only, not iframe / HTML code.
          </span>
        )}
        {(status.kind === "valid-vimeo" || status.kind === "valid-youtube") && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="ml-auto h-7"
            onClick={() => setShowPreview((s) => !s)}
            data-testid={`${testId}-preview-btn`}
          >
            <Eye className="h-3.5 w-3.5" />
            {showPreview ? "Close Preview" : "Preview"}
          </Button>
        )}
      </div>

      <p className="text-xs text-muted-foreground leading-relaxed">{helperText}</p>

      {showPreview && status.kind === "valid-vimeo" && (
        <div
          className="relative w-full overflow-hidden rounded-xl border border-border bg-black"
          style={{ paddingBottom: "56.25%" }}
          data-testid="vimeo-preview-frame"
        >
          <iframe
            src={status.embed}
            className="absolute inset-0 h-full w-full"
            frameBorder="0"
            allow="autoplay; fullscreen; picture-in-picture"
            allowFullScreen
            title="Vimeo preview"
          />
        </div>
      )}
    </div>
  );
}
